/**
 * Document Expiration Utilities
 * 
 * Provides utilities for calculating and displaying document expiration status
 */

import type { Document } from '@/services/documentVault';

export type ExpirationStatus = {
  status: 'valid' | 'expiring_soon' | 'expiring_very_soon' | 'expired';
  daysUntil: number;
  color: string;
  label: string;
  urgency: 'low' | 'medium' | 'high' | 'critical';
};

// Thresholds for expiration warnings (in days)
const EXPIRING_SOON_THRESHOLD = 90; // Yellow warning
const EXPIRING_VERY_SOON_THRESHOLD = 30; // Orange/red warning
const EXPIRED_THRESHOLD = 0; // Red/critical

/**
 * Calculate expiration status for a document
 */
export function getExpirationStatus(
  expiresOn: Date | null | undefined
): ExpirationStatus | null {
  if (!expiresOn) {
    return null;
  }

  const now = new Date();
  const expirationDate = new Date(expiresOn);
  const diffTime = expirationDate.getTime() - now.getTime();
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysUntil < EXPIRED_THRESHOLD) {
    return {
      status: 'expired',
      daysUntil: Math.abs(daysUntil),
      color: '#FF3B30', // Red
      label: 'Expired',
      urgency: 'critical',
    };
  }

  if (daysUntil <= EXPIRING_VERY_SOON_THRESHOLD) {
    return {
      status: 'expiring_very_soon',
      daysUntil,
      color: '#FF9500', // Orange
      label: 'Expiring Soon',
      urgency: 'high',
    };
  }

  if (daysUntil <= EXPIRING_SOON_THRESHOLD) {
    return {
      status: 'expiring_soon',
      daysUntil,
      color: '#FFCC00', // Yellow
      label: 'Expiring Soon',
      urgency: 'medium',
    };
  }

  return {
    status: 'valid',
    daysUntil,
    color: '#34C759', // Green
    label: 'Valid',
    urgency: 'low',
  };
}

/**
 * Get expiration date from document (checks both expiresOn and metadata)
 */
export function getDocumentExpirationDate(document: Document): Date | null {
  // First check the top-level expiresOn field
  if (document.expiresOn) {
    return document.expiresOn;
  }

  // Then check metadata for expirationDate
  if (document.metadata) {
    const meta = document.metadata;
    
    // Check for expirationDate in various metadata types
    if ('expirationDate' in meta && meta.expirationDate instanceof Date) {
      return meta.expirationDate;
    }
    
    // Check for coverageEndDate in insurance metadata
    if ('coverageEndDate' in meta && meta.coverageEndDate instanceof Date) {
      return meta.coverageEndDate;
    }
  }

  return null;
}

/**
 * Get all documents with expiration status
 */
export function getDocumentsWithExpirationStatus(
  documents: Document[]
): Array<Document & { expirationStatus: ExpirationStatus | null }> {
  return documents.map((doc) => {
    const expirationDate = getDocumentExpirationDate(doc);
    const expirationStatus = getExpirationStatus(expirationDate);
    return {
      ...doc,
      expirationStatus,
    };
  });
}

/**
 * Filter documents by expiration status
 */
export function filterDocumentsByExpiration(
  documents: Document[],
  status: ExpirationStatus['status'] | 'all'
): Document[] {
  if (status === 'all') {
    return documents;
  }

  const docsWithStatus = getDocumentsWithExpirationStatus(documents);
  return docsWithStatus
    .filter((doc) => doc.expirationStatus?.status === status)
    .map(({ expirationStatus, ...doc }) => doc);
}

/**
 * Get documents expiring within a certain number of days
 */
export function getDocumentsExpiringWithin(
  documents: Document[],
  days: number
): Document[] {
  const now = new Date();
  const thresholdDate = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);

  return documents.filter((doc) => {
    const expirationDate = getDocumentExpirationDate(doc);
    if (!expirationDate) return false;
    
    return expirationDate <= thresholdDate && expirationDate >= now;
  });
}

/**
 * Get expired documents
 */
export function getExpiredDocuments(documents: Document[]): Document[] {
  const now = new Date();

  return documents.filter((doc) => {
    const expirationDate = getDocumentExpirationDate(doc);
    if (!expirationDate) return false;
    
    return expirationDate < now;
  });
}

/**
 * Sort documents by expiration date (soonest first)
 */
export function sortDocumentsByExpiration(
  documents: Document[],
  ascending: boolean = true
): Document[] {
  return [...documents].sort((a, b) => {
    const dateA = getDocumentExpirationDate(a);
    const dateB = getDocumentExpirationDate(b);

    // Documents without expiration dates go to the end
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    const diff = dateA.getTime() - dateB.getTime();
    return ascending ? diff : -diff;
  });
}

